<script setup lang="ts">
/**
 * RoleAvatar - 军师头像组件.
 * 自动判断 avatar 值:
 *   - 含 ":" (如 "heroicons:light-bulb-solid") -> 用 Iconify 渲染
 *   - URL (http.../data:...) -> 用 <img> 渲染
 *   - 空 -> 显示名称首字
 */
import { computed } from 'vue'
import { Icon as IconifyIcon } from '@iconify/vue'
import { avatarText } from '@/utils/format'

const props = withDefaults(
  defineProps<{
    avatar?: string | null
    name?: string
    /** 尺寸 (px) */
    size?: number
    /** 图标在头像中的占比 (0-1) */
    iconRatio?: number
    /** 圆角: 'full' (圆形) | 'lg' (圆角方) */
    rounded?: 'full' | 'lg'
  }>(),
  { size: 32, iconRatio: 0.6, rounded: 'full' },
)

const isIcon = computed(() => !!props.avatar && props.avatar.includes(':'))
const iconSize = computed(() => Math.round(props.size * props.iconRatio))
</script>

<template>
  <span
    class="flex shrink-0 items-center justify-center overflow-hidden bg-primary/8 text-primary"
    :class="rounded === 'full' ? 'rounded-full' : 'rounded-2xl'"
    :style="{ width: size + 'px', height: size + 'px' }"
  >
    <IconifyIcon
      v-if="isIcon"
      :icon="avatar!"
      :style="{ width: iconSize + 'px', height: iconSize + 'px' }"
    />
    <img
      v-else-if="avatar"
      :src="avatar"
      :alt="name ?? ''"
      class="h-full w-full object-cover"
    />
    <span
      v-else
      :style="{ fontSize: Math.round(size * 0.4) + 'px' }"
      class="font-medium"
    >
      {{ avatarText(name ?? '?') }}
    </span>
  </span>
</template>
